import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Filter, ArrowLeft, Users } from "lucide-react";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";

const STATUS_OPTIONS = ["new", "contacted", "closed"];
const TYPE_OPTIONS = ["", "gold", "silver", "land"];

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads", {
        params: {
          type: filterType || undefined,
          status: filterStatus || undefined,
          city: filterCity || undefined,
        },
      });
      setItems(data.items || []);
      setTotals(data.totals || totals);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterStatus]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setItems((s) => s.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success("Status updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const saveNote = async (id) => {
    try {
      await api.patch(`/admin/leads/${id}`, {
        status: items.find((x) => x.id === id)?.status || "new",
        note: noteDraft,
      });
      setItems((s) =>
        s.map((it) => (it.id === id ? { ...it, admin_note: noteDraft } : it)),
      );
      setEditingId(null);
      setNoteDraft("");
      toast.success("Note saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note");
    }
  };

  const download = async () => {
    try {
      const token = localStorage.getItem("stock_token");
      const params = new URLSearchParams();
      if (filterType) params.append("type", filterType);
      if (filterStatus) params.append("status", filterStatus);
      const url = `${API}/admin/leads/export?${params.toString()}`;
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `fairvest_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e?.message || "Export failed");
    }
  };

  return (
    <div className="min-h-screen bg-bone-50" data-testid="admin-page">
      <header className="border-b border-line bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Link
              to="/dashboard"
              className="text-stone_brand-light hover:text-stone_brand-dark text-xs sm:text-sm inline-flex items-center gap-1 flex-shrink-0"
              data-testid="admin-back"
            >
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <div className="min-w-0 flex-1">
              <div className="overline text-moss text-[0.6rem] sm:text-[0.65rem]">— Admin</div>
              <div className="font-serif-display text-base sm:text-xl text-stone_brand-dark font-medium truncate">
                Buyer leads
              </div>
            </div>
          </div>
          <Button
            onClick={download}
            className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50 gap-1.5 h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm flex-shrink-0"
            data-testid="admin-export-csv"
          >
            <Download className="w-4 h-4" /> <span className="hidden sm:inline">Export CSV</span><span className="sm:hidden">CSV</span>
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Stat tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Total", value: totals.total, key: "" },
            { label: "New", value: totals.new, key: "new" },
            { label: "Contacted", value: totals.contacted, key: "contacted" },
            { label: "Closed", value: totals.closed, key: "closed" },
          ].map((t) => (
            <button
              key={t.label}
              onClick={() => setFilterStatus(t.key)}
              className={
                "text-left border rounded-sm p-4 transition " +
                (filterStatus === t.key
                  ? "border-moss bg-moss-bg/40"
                  : "border-line bg-white hover:border-stone_brand")
              }
              data-testid={`admin-stat-${t.label.toLowerCase()}`}
            >
              <div className="text-[0.65rem] font-mono uppercase tracking-widest text-stone_brand-light">
                {t.label}
              </div>
              <div className="font-serif-display text-3xl text-stone_brand-dark font-medium mt-1">
                {t.value}
              </div>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4 items-center" data-testid="admin-filters">
          <Filter className="w-4 h-4 text-stone_brand-light" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="h-9 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
            data-testid="admin-filter-type"
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t || "all"} value={t}>
                {t ? t.charAt(0).toUpperCase() + t.slice(1) : "All types"}
              </option>
            ))}
          </select>
          <input
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchLeads()}
            placeholder="Filter by city…"
            className="h-9 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
            data-testid="admin-filter-city"
          />
          <button
            onClick={fetchLeads}
            className="h-9 px-3 text-xs font-mono uppercase tracking-wider rounded-sm border border-line bg-white text-stone_brand-dark hover:border-stone_brand"
            data-testid="admin-apply-filters"
          >
            Apply
          </button>
          {(filterType || filterStatus || filterCity) && (
            <button
              onClick={() => {
                setFilterType("");
                setFilterStatus("");
                setFilterCity("");
              }}
              className="h-9 px-3 text-xs font-mono uppercase tracking-wider text-stone_brand-light hover:text-terracotta"
              data-testid="admin-clear-filters"
            >
              Clear
            </button>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-sm text-stone_brand-light py-8 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-line rounded-sm p-10 text-center">
            <Users className="w-8 h-8 text-stone_brand-light mx-auto mb-2" />
            <div className="text-sm text-stone_brand-light">
              No leads matching the filter.
            </div>
          </div>
        ) : (
          <div className="border border-line rounded-sm bg-white overflow-x-auto" data-testid="admin-leads-table">
            <table className="w-full text-sm">
              <thead className="bg-bone border-b border-line">
                <tr className="text-left text-[0.65rem] font-mono uppercase tracking-widest text-stone_brand-light">
                  <th className="px-4 py-2.5">Created</th>
                  <th className="px-4 py-2.5">Type</th>
                  <th className="px-4 py-2.5">Email</th>
                  <th className="px-4 py-2.5">City</th>
                  <th className="px-4 py-2.5">Note</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Internal note</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className="border-t border-line hover:bg-bone-50"
                    data-testid={`admin-lead-row-${it.id}`}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs text-stone_brand-light whitespace-nowrap">
                      {(it.created_at || "").slice(0, 10)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-[0.7rem] font-mono uppercase tracking-wider text-amber_brand">
                        {it.type}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-stone_brand-dark">{it.email || "—"}</td>
                    <td className="px-4 py-2.5 text-stone_brand-dark">{it.location || "—"}</td>
                    <td className="px-4 py-2.5 text-stone_brand-dark text-xs max-w-[220px] truncate" title={it.note}>
                      {it.note || "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <select
                        value={it.status || "new"}
                        onChange={(e) => updateStatus(it.id, e.target.value)}
                        className={
                          "h-8 px-2 text-xs rounded-sm border font-mono uppercase " +
                          (it.status === "closed"
                            ? "border-stone_brand-light bg-bone text-stone_brand-light"
                            : it.status === "contacted"
                              ? "border-amber_brand bg-amber_brand-bg text-stone_brand-dark"
                              : "border-moss bg-moss-bg text-moss")
                        }
                        data-testid={`admin-status-${it.id}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {editingId === it.id ? (
                        <div className="flex gap-1 items-center">
                          <input
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && saveNote(it.id)}
                            className="h-8 px-2 text-xs rounded-sm border border-line focus:outline-none focus:ring-1 focus:ring-moss min-w-[160px]"
                            autoFocus
                            data-testid={`admin-note-input-${it.id}`}
                          />
                          <button
                            onClick={() => saveNote(it.id)}
                            className="text-moss text-xs hover:underline"
                          >
                            save
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingId(it.id);
                            setNoteDraft(it.admin_note || "");
                          }}
                          className="text-stone_brand-light hover:text-stone_brand-dark text-xs italic max-w-[180px] truncate block"
                          data-testid={`admin-note-btn-${it.id}`}
                        >
                          {it.admin_note || "+ add note"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
